import { useState, useEffect } from "react";
import { Save, Loader2, Globe, Bot, SlidersHorizontal, BellRing, Building2, Mail, MessageSquare, PlugZap, Newspaper, Megaphone, MessageCircle, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { getSettings, saveSettings, testEmail, testSms, testInfocamere, testWebSearch, testWhatsapp, getChatUsage } from "@/lib/api";
import LandingPreview from "@/components/LandingPreview";
import { toast } from "sonner";

const LANDING_FIELDS = [
  ["badge", "Badge sopra il titolo"], ["titolo", "Titolo (parte iniziale)"],
  ["titolo_evidenziato", "Titolo (parola evidenziata)"], ["titolo_coda", "Titolo (parte finale)"],
  ["sottotitolo", "Sottotitolo", true], ["cta", "Testo bottone principale"], ["cta_nota", "Nota accanto al bottone"],
  ["chat_titolo", "Titolo sezione chat"], ["chat_sottotitolo", "Sottotitolo sezione chat", true],
  ["visione_titolo", "Titolo sezione visione", true], ["footer_testo", "Testo footer", true],
  ["contatto_email", "Email di contatto"], ["contatto_telefono", "Telefono di contatto"],
];
const ACCHIAPPO_FIELDS = [
  ["nome_brand", "Nome del brand (sito acchiappo)"], ["badge", "Badge sopra il titolo"],
  ["titolo", "Titolo"], ["titolo_evidenziato", "Titolo (parte evidenziata)"], ["sottotitolo", "Sottotitolo", true],
  ["cta", "Testo bottone"], ["cta_nota", "Nota accanto al bottone"],
  ["punti", "Punti di forza (separati da |)", true],
  ["chat_titolo", "Titolo sezione chat"], ["chat_sottotitolo", "Sottotitolo sezione chat", true],
  ["footer_testo", "Testo footer", true], ["contatto_telefono", "Telefono mostrato in alto"],
  ["dominio", "Dominio dedicato (es. quintosubito.it) — quando lo colleghi, la home di quel dominio mostra questo sito"],
];
const LIMITI_FIELDS = [
  ["max_sessioni_ip_giorno", "Max verifiche per IP al giorno", false, "number"],
  ["max_sessioni_giorno", "Max verifiche totali al giorno", false, "number"],
  ["max_messaggi_sessione", "Max messaggi per intervista", false, "number"],
  ["max_messaggi_giorno", "Max messaggi totali al giorno", false, "number"],
];
const WHATSAPP_FIELDS = [
  ["nome_consulente", "Nome del consulente che richiama"], ["fascia_richiamo", "Quando richiami (es. entro 24 ore lavorative)"],
  ["twilio_whatsapp_from", "Numero WhatsApp Twilio mittente (+39...)"],
];
const CHATBOT_FIELDS = [
  ["nome_assistente", "Nome dell'assistente"], ["saluto", "Messaggio di benvenuto", true],
  ["tono", "Tono"], ["max_frasi", "Max frasi per messaggio", false, "number"],
  ["istruzioni_extra", "Istruzioni aggiuntive per il chatbot (regole, prodotti, promozioni...)", true],
];
const SOGLIE_FIELDS = [
  ["netto_minimo", "Netto minimo (€)"], ["minimo_vitale", "Minimo vitale (€)"],
  ["quinto_ratio", "Quota massima rata (0.20 = 20%)"], ["rata_minima", "Rata minima (€)"],
];
const NOTIF_TEXT = [
  ["email_consulente", "Email consulente (riceve i richiami)", false, "email"],
  ["telefono_consulente", "Telefono consulente (+39...)"],
];
const TWILIO_FIELDS = [
  ["twilio_sid", "Twilio Account SID"], ["twilio_token", "Twilio Auth Token", false, "password"], ["twilio_from", "Numero mittente Twilio (+1... / +39...)"],
];
const NOTIF_SWITCHES = [
  ["email_nuovo_lead", "Email immediata al consulente per ogni nuovo lead (con trascrizione e allerta azienda)"],
  ["notifica_cliente", "Avvisa anche il cliente via email"],
  ["sms_consulente", "SMS al consulente (richiede Twilio)"],
  ["sms_cliente", "SMS al cliente (richiede Twilio)"],
];

const Field = ({ section, k, label, multi, type, value, onChange }) => (
  <div>
    <label className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</label>
    {multi ? (
      <Textarea data-testid={`setting-${section}-${k}`} value={value ?? ""} rows={3}
        onChange={(e) => onChange(section, k, e.target.value)}
        className="mt-1 bg-slate-950 border-white/10 text-white" />
    ) : (
      <Input data-testid={`setting-${section}-${k}`} value={value ?? ""} type={type || "text"} step="any" autoComplete="off"
        onChange={(e) => onChange(section, k, type === "number" ? Number(e.target.value) : e.target.value)}
        className="mt-1 bg-slate-950 border-white/10 text-white" />
    )}
  </div>
);

const Section = ({ icon: Icon, title, desc, children, action }) => (
  <div className="rounded-xl bg-slate-900 border border-white/10 p-4 sm:p-6 space-y-4 min-w-0 overflow-hidden break-words">
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-accent" />
        <div>
          <h3 className="font-display font-semibold text-white">{title}</h3>
          <p className="text-xs text-slate-400">{desc}</p>
        </div>
      </div>
      {action}
    </div>
    {children}
  </div>
);

const TestButton = ({ testid, label, icon: Icon, onClick, busy }) => (
  <Button data-testid={testid} size="sm" variant="outline" disabled={busy} onClick={onClick}
    className="gap-2 border-white/10 text-slate-200 hover:bg-slate-800 shrink-0">
    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />} {label}
  </Button>
);

export default function SettingsPanel({ isAdmin }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [usage, setUsage] = useState(null);

  useEffect(() => { getSettings().then(setData).catch(() => toast.error("Impossibile caricare le impostazioni")); getChatUsage().then(setUsage).catch(() => {}); }, []);

  const onChange = (section, k, v) => setData((d) => ({ ...d, [section]: { ...d[section], [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      setData(await saveSettings(data));
      toast.success("Impostazioni salvate: sito e chatbot aggiornati");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  const runTest = async (key, fn, okMsg) => {
    setTesting(key);
    try {
      await saveSettings(data);
      const r = await fn();
      toast.success(okMsg(r));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test non riuscito");
    } finally {
      setTesting(null);
    }
  };

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;

  const render = (section, fields) => fields.map(([k, label, multi, type]) => (
    <Field key={k} section={section} k={k} label={label} multi={multi} type={type} value={data[section][k]} onChange={onChange} />
  ));

  return (
    <div data-testid="settings-panel" className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-400">Modifica testi del sito, chatbot, notifiche e integrazioni: le modifiche sono attive subito, senza toccare il codice.</p>
        {isAdmin && (
          <Button data-testid="settings-save-button" onClick={save} disabled={saving} className="gap-2 bg-accent hover:bg-accent/90 text-white shrink-0">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
          </Button>
        )}
      </div>

      <div className="grid xl:grid-cols-2 gap-6">
        <div className="space-y-6">
          <Section icon={Globe} title="Sito pubblico" desc="Testi della landing page">{render("landing", LANDING_FIELDS)}</Section>
          <Section icon={Megaphone} title="Sito acchiappo (/prestito)" desc="Pagina di acquisizione clienti con brand separato: i lead arrivano in questo CRM con origine 'acchiappo'">
            <a href="/prestito" target="_blank" rel="noreferrer" className="text-xs text-accent underline" data-testid="acchiappo-open-link">Apri /prestito in una nuova scheda</a>
            {render("acchiappo", ACCHIAPPO_FIELDS)}
          </Section>
          <Section icon={Bot} title="Chatbot" desc="Nome, saluto, tono e istruzioni aggiuntive dell'assistente AI">{render("chatbot", CHATBOT_FIELDS)}</Section>
        </div>
        <div className="space-y-6 xl:sticky xl:top-24 self-start">
          <LandingPreview landing={data.landing} chatbot={data.chatbot} />
          <Section icon={SlidersHorizontal} title="Soglie di qualificazione" desc="Parametri del motore di scoring">
            <div className="grid sm:grid-cols-2 gap-4">{render("soglie", SOGLIE_FIELDS)}</div>
          </Section>
        </div>
      </div>

      <div className="grid xl:grid-cols-2 gap-6">
        <Section icon={BellRing} title="Richiami automatici" desc="Email (inclusa, nessuna configurazione) e SMS via Twilio, 4 mesi prima del rinnovo"
          action={isAdmin && (
            <div className="flex gap-2">
              <TestButton testid="settings-test-email" label="Test email" icon={Mail} busy={testing === "email"}
                onClick={() => runTest("email", testEmail, (r) => `Email di prova inviata a ${r.to}`)} />
              <TestButton testid="settings-test-sms" label="Test SMS" icon={MessageSquare} busy={testing === "sms"}
                onClick={() => runTest("sms", testSms, () => "SMS di prova inviato")} />
            </div>
          )}>
          <div className="grid sm:grid-cols-2 gap-4">{render("notifiche", NOTIF_TEXT)}</div>
          <div className="space-y-3 pt-1">
            {NOTIF_SWITCHES.map(([k, label]) => (
              <label key={k} className="flex items-center justify-between gap-3 text-sm text-slate-200">
                <span>{label}</span>
                <Switch data-testid={`setting-notifiche-${k}`} checked={!!data.notifiche[k]} onCheckedChange={(v) => onChange("notifiche", k, v)} />
              </label>
            ))}
          </div>
          <div className="pt-2 border-t border-white/10">
            <p className="text-xs text-slate-400 mb-3">Credenziali Twilio (console.twilio.com). Il token resta mascherato dopo il salvataggio.</p>
            <div className="grid sm:grid-cols-3 gap-4">{render("notifiche", TWILIO_FIELDS)}</div>
          </div>
          <div className="pt-3 border-t border-white/10 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-white flex items-center gap-2"><MessageCircle className="h-4 w-4 text-emerald-400" /> WhatsApp al cliente a fine intervista</div>
                <p className="text-xs text-slate-400">Messaggio automatico con nome del consulente e tempi di richiamo (Twilio WhatsApp). Il bottone "Scrivici su WhatsApp" nella schermata esito usa il telefono consulente.</p>
              </div>
              {isAdmin && <TestButton testid="settings-test-whatsapp" label="Invia test" icon={MessageCircle} busy={testing === "wa"}
                onClick={() => runTest("wa", testWhatsapp, () => "WhatsApp di prova inviato al consulente")} />}
            </div>
            <label className="flex items-center justify-between gap-3 text-sm text-slate-200">
              <span>Invia WhatsApp automatico al cliente (richiede Twilio WhatsApp)</span>
              <Switch data-testid="setting-notifiche-whatsapp_cliente" checked={!!data.notifiche.whatsapp_cliente} onCheckedChange={(v) => onChange("notifiche", "whatsapp_cliente", v)} />
            </label>
            <div className="grid sm:grid-cols-3 gap-4">{render("notifiche", WHATSAPP_FIELDS)}</div>
          </div>
        </Section>

        <Section icon={ShieldAlert} title="Protezione anti-abuso chat" desc="Tetti giornalieri per evitare costi AI e lead finti. Oltre il tetto la chat risponde 'riprova più tardi'">
          {usage && (
            <div className="grid grid-cols-3 gap-3 text-center" data-testid="chat-usage">
              {[["Verifiche oggi", usage.sessioni], ["Messaggi oggi", usage.messaggi], ["IP attivi", usage.ip_attivi]].map(([l, v]) => (
                <div key={l} className="rounded-lg bg-slate-800/60 p-3"><div className="font-mono text-2xl font-bold text-white">{v}</div><div className="text-xs text-slate-400">{l}</div></div>
              ))}
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">{render("limiti", LIMITI_FIELDS)}</div>
        </Section>

        <Section icon={Building2} title="Dati aziendali reali (InfoCamere)" desc="Registro Imprese via Openapi.it. Senza token viene usata la stima AI come fallback"
          action={isAdmin && (
            <TestButton testid="settings-test-infocamere" label="Verifica connessione" icon={PlugZap} busy={testing === "ic"}
              onClick={() => runTest("ic", testInfocamere, (r) => r.ok ? "Connessione InfoCamere OK" : `Risposta ${r.status}: ${r.detail}`)} />
          )}>
          {render("integrazioni", [["openapi_token", "Token Openapi.it", false, "password"], ["openapi_base_url", "Endpoint API"]])}
          <p className="text-xs text-slate-500">Fonte attiva: <span className="text-slate-300">{data.integrazioni.openapi_token ? "InfoCamere / Registro Imprese" : "Stima AI indicativa (fallback)"}</span></p>
          <div className="pt-3 border-t border-white/10 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-white flex items-center gap-2"><Newspaper className="h-4 w-4 text-accent" /> Ricerca web e notizie (Tavily)</div>
                <p className="text-xs text-slate-400">Notizie reali e recenti su crisi, CIG, licenziamenti: API key gratuita su app.tavily.com (1.000 ricerche/mese).</p>
              </div>
              {isAdmin && <TestButton testid="settings-test-web" label="Verifica" icon={PlugZap} busy={testing === "web"}
                onClick={() => runTest("web", testWebSearch, (r) => `Ricerca web OK (${r.fonti} fonti trovate)`)} />}
            </div>
            {render("integrazioni", [["tavily_api_key", "API key Tavily", false, "password"]])}
            <p className="text-xs text-slate-500">Stato: <span className="text-slate-300">{data.integrazioni.tavily_api_key ? "attiva — le allerte usano notizie di oggi" : "non attiva — solo memoria AI"}</span></p>
          </div>
        </Section>
      </div>
    </div>
  );
}
